// A dialog built on the browser's own <dialog>: it traps focus, closes on Escape and makes the page behind it
// inert, all without any code here. The parent decides when it exists (mount = open, unmount = closed).

import { useEffect, useEffectEvent, useRef, type ReactNode } from 'react';
import { Icon } from './Icon';

interface ModalProps {
  title: ReactNode;
  /** Id for the title, so the dialog is announced by name. */
  titleId: string;
  wide?: boolean;
  /** Asked for by Escape, the X button or a click on the backdrop. The parent should stop rendering the Modal. */
  onClose: () => void;
  children: ReactNode;
}

export function Modal({ title, titleId, wide = false, onClose, children }: ModalProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const closed = useEffectEvent(onClose);

  useEffect(() => {
    const dlg = dialog.current!;
    const opener = document.activeElement as HTMLElement | null;
    // The browser reports a close a moment after it happens. If we closed and reopened the dialog in between (React's
    // development checks do exactly that), that late report is stale: the dialog is open again and must stay open.
    const onNativeClose = () => {
      if (!dlg.open) closed();
    };
    dlg.addEventListener('close', onNativeClose);
    dlg.showModal();
    return () => {
      // Stop listening first: closing it ourselves must not look like the user closing it.
      dlg.removeEventListener('close', onNativeClose);
      if (dlg.open) dlg.close();
      if (opener && document.contains(opener)) opener.focus();
    };
  }, []);

  return (
    <dialog
      ref={dialog}
      className={`dialog${wide ? ' dialog-wide' : ''}`}
      aria-labelledby={titleId}
      onClick={(e) => {
        // Only the backdrop itself, not a click that started inside the dialog.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog-inner">
        <header className="dialog-head">
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close dialog">
            <Icon name="x" />
          </button>
        </header>
        <div className="dialog-body">{children}</div>
      </div>
    </dialog>
  );
}
